import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import apiClient from "../lib/api-client";
import { setAuthToken, AUTH_TOKEN_STORAGE_KEY } from "../lib/api-client";
import {
  IMPERSONATION_ORIGIN_TOKEN_KEY,
  IMPERSONATION_TARGET_KEY,
} from "../hooks/useImpersonation";
import type { PermissionKey } from "../lib/permissions";

const TOKEN_KEY = AUTH_TOKEN_STORAGE_KEY;
/** "Recordarme" (14-sep-2026): con la marca puesta, el token Bearer vive en
 *  localStorage (sobrevive a cerrar la PWA — en iOS la cookie es de terceros
 *  y Safari la bloquea, así que el Bearer ES la sesión); sin ella, en
 *  sessionStorage como siempre (muere al cerrar). */
const REMEMBER_KEY = "rm_auth_remember";

function isRemembered(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) === "1";
  } catch {
    return false;
  }
}

/** Token guardado, venga del almacén que venga (sesión primero). */
function readStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Guarda el token en el almacén que toque según "Recordarme" y limpia el
 *  otro (nunca dos copias divergentes — lección del token zombi en
 *  localStorage que daba "Token inválido"). */
function persistToken(token: string) {
  try {
    if (isRemembered()) {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Almacenamiento bloqueado (privado/limpiado): queda solo en memoria.
  }
}

function clearStoredToken() {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // nada
  }
}

/** Borra cualquier estado residual de impersonación (ver useImpersonation.ts). */
function clearImpersonationState() {
  sessionStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN_KEY);
  sessionStorage.removeItem(IMPERSONATION_TARGET_KEY);
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: string;
  /**
   * Set EFECTIVO de permisos del usuario (owner/admin reciben las 16 claves
   * del catálogo, ver `lib/permissions.ts`). Viene ya resuelto por el backend
   * en `/auth/me` y en login — el frontend NO lo calcula, solo lo pinta.
   * Opcional por si algún flujo antiguo de `setUser` no lo incluyera todavía.
   */
  permissions?: PermissionKey[];
  organization?: {
    id: string;
    name: string;
    slug: string;
    bookingMode?: "tables" | "appointments";
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  socialLogin: (
    provider: "google" | "apple",
    idToken: string,
    name?: { firstName?: string; lastName?: string },
  ) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Don't try to fetch user if we're on public auth pages
    if (
      window.location.pathname === "/login" ||
      window.location.pathname === "/register" ||
      window.location.pathname === "/forgot-password" ||
      window.location.pathname === "/reset-password"
    ) {
      setIsLoading(false);
      return;
    }

    // Restaura el token: sessionStorage (sesión normal) o localStorage
    // ("Recordarme" — sobrevive a cerrar la PWA/pestaña).
    const savedToken = readStoredToken();
    if (savedToken) {
      setAuthToken(savedToken);
    }

    // Always attempt to fetch the current user
    // Uses cookie (if set) OR Bearer token from memory as fallback
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get("/auth/me");
      setUser(response.data.user);
      // If /me succeeds and we got a token back, store it
      if (response.data.token) {
        setAuthToken(response.data.token);
        persistToken(response.data.token);
      }
    } catch (error) {
      setUser(null);
      setAuthToken(null);
      clearStoredToken();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe = false) => {
    // Defensivo: un login nuevo nunca debe arrastrar estado de impersonación residual.
    clearImpersonationState();

    // El backend alarga el JWT a 30 días con rememberMe.
    const response = await apiClient.post("/auth/login", { email, password, rememberMe });
    const { user, token } = response.data;
    if (token) {
      try {
        if (rememberMe) localStorage.setItem(REMEMBER_KEY, "1");
        else localStorage.removeItem(REMEMBER_KEY);
      } catch {
        // sin almacenamiento: el token quedará solo en memoria
      }
      setAuthToken(token);
      persistToken(token);
    }
    setUser(user);
  };

  const socialLogin = async (
    provider: "google" | "apple",
    idToken: string,
    name?: { firstName?: string; lastName?: string },
  ) => {
    // Defensivo: un login nuevo nunca debe arrastrar estado de impersonación residual.
    clearImpersonationState();

    const response = await apiClient.post("/auth/web-social", {
      provider,
      idToken,
      name,
    });
    const { user, token } = response.data;
    if (token) {
      try {
        localStorage.setItem(REMEMBER_KEY, "1");
      } catch {
        // sin almacenamiento: solo memoria
      }
      setAuthToken(token);
      persistToken(token);
    }
    setUser(user);
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear cookie on server
      await apiClient.post("/auth/logout");
    } catch (error) {
      // Ignore errors, we're logging out anyway
    }

    // Clear all auth state
    setAuthToken(null);
    clearStoredToken();
    // Un "Cerrar sesión" estando impersonando no debe dejar estado residual
    // que confunda una sesión futura (ver useImpersonation.ts).
    clearImpersonationState();
    localStorage.removeItem("rm_current_location_id");
    localStorage.removeItem("rm_current_organization_id");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        socialLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
